import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { programs } from "@/mocks/programs";
import { useTranslatedList } from "@/hooks/useTranslatedList";

export default function FeaturedProgramsSection() {
  const { t } = useTranslation();
  const featuredPrograms = useTranslatedList(programs, ["name", "description", "category"])
    .filter((p) => p.featured)
    .slice(0, 4);

  const dayMap: Record<string, string> = {
    Monday: "Mon",
    Tuesday: "Tue",
    Wednesday: "Wed",
    Thursday: "Thu",
    Friday: "Fri",
    Saturday: "Sat",
    Sunday: "Sun",
  };

  return (
    <section className="bg-background-100 py-12 md:py-16">
      <div className="ltv-container">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8">
          <h2 className="ltv-section-title mb-0">{t("home.featuredPrograms")}</h2>
          <Link
            to="/programs"
            className="inline-flex items-center gap-1 text-sm font-semibold text-accent-500 hover:text-accent-600 transition-colors whitespace-nowrap"
          >
            {t("home.viewAllPrograms")}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {featuredPrograms.map((program) => (
            <Link
              key={program.id}
              to={`/programs/${program.slug}`}
              className="ltv-card group"
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={program.image}
                  alt={program.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3">
                  <span className="bg-accent-500 text-white text-xs font-semibold px-2 py-1 rounded">
                    {program.category}
                  </span>
                </div>
                <div className="absolute bottom-3 right-3">
                  <span className="bg-black/60 text-white text-xs font-medium px-2 py-1 rounded">
                    {program.duration}
                  </span>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-foreground-900 leading-snug mb-1 group-hover:text-accent-600 transition-colors">
                  {program.name}
                </h3>
                <p className="text-sm text-foreground-600 line-clamp-2 mb-3">
                  {program.description}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs text-foreground-500">
                    <i className="ri-user-line"></i>
                    <span>{program.presenter}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    {program.broadcastDays.map((day) => (
                      <span
                        key={day}
                        className="text-[10px] font-semibold bg-primary-50 text-primary-700 px-1.5 py-0.5 rounded"
                      >
                        {dayMap[day] || day}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}